import { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Fade,
} from '@mui/material';
import { useHealthWalletSync } from './healthWalletSyncService';
import { useGetOnboardingStatusQuery } from './healthWalletSlice';
import { DataRequestStatus } from '../../types/onboarding';

/**
 * Tiny clock that keeps REAL time (Leo's ask — the stock AccessTimeIcon is a frozen SVG):
 *  - hour hand: set from the wall clock at each render (the pill re-renders every second
 *    while the stopwatch ticks, so it stays honest during a run);
 *  - second hand: one revolution per minute in discrete steps(60) — a watch tick, not a
 *    spinner sweep — synced to actual wall-clock seconds via a negative animation delay;
 *    when the run terminates it freezes at the stop-time position (static transform).
 *  - whole dial: gentle opacity pulse while running (brought back alongside the hand).
 * Inherits `color` (the pill's state color).
 */
const TickingClockIcon: FC<{ color: string; running: boolean }> = ({ color, running }) => {
  const now = new Date();
  const hourAngle = (now.getHours() % 12) * 30 + now.getMinutes() * 0.5;
  const seconds = now.getSeconds();
  return (
    <Box
      component="svg"
      viewBox="0 0 24 24"
      sx={{
        width: '1rem',
        height: '1rem',
        color,
        flexShrink: 0,
        animation: running ? 'hwStopwatchPulse 1.6s ease-in-out infinite' : 'none',
        '@keyframes hwStopwatchPulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.45 },
        },
      }}
    >
      <circle cx="12" cy="12" r="9.5" fill="none" stroke="currentColor" strokeWidth="1.8" />
      {/* hour hand — actual time */}
      <line
        x1="12" y1="12" x2="12" y2="8.6"
        stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"
        transform={`rotate(${hourAngle} 12 12)`}
      />
      {/* second hand — actual seconds; ticking while running, frozen at stop otherwise */}
      <Box
        component="line"
        x1="12"
        y1="12"
        x2="12"
        y2="6.2"
        sx={{
          stroke: 'currentColor',
          strokeWidth: 1.6,
          strokeLinecap: 'round',
          transformOrigin: '12px 12px',
          transform: `rotate(${seconds * 6}deg)`, // static position (overridden by the animation while running)
          animation: running ? 'hwClockTick 60s steps(60) infinite' : 'none',
          animationDelay: `-${seconds}s`, // start mid-rotation = real wall-clock seconds
          '@keyframes hwClockTick': {
            from: { transform: 'rotate(0deg)' },
            to: { transform: 'rotate(360deg)' },
          },
        }}
      />
    </Box>
  );
};

/**
 * Stopwatch for the in-flight workflow, driven by the DB-computed `dataRequestElapsedSeconds`
 * (server-side `now() − requested_at` while running, `fulfilled_at − requested_at` when terminal).
 * All time arithmetic happens on the DB clock, so there is NO client↔DB clock-skew to fight:
 *   • running → SEED from the server elapsed, then increment LOCALLY. The local increment is a
 *     DURATION (seconds since we received the value), not an absolute-time comparison, so it's
 *     skew-immune in both directions. Each `/onboarding/status` refetch re-seeds, so a mid-run
 *     page reload resumes at the TRUE elapsed (the DB recomputes now() − requested_at). See
 *     project_hw_sync_stopwatch.
 *   • terminal → show the frozen server elapsed as-is (exact DB `fulfilled_at − requested_at`).
 */
function useSyncStopwatch(): { elapsedSeconds: number; running: boolean; dataRequestStatus?: DataRequestStatus } {
  // Shared RTK cache entry — no extra HTTP call from this subscriber.
  const { data: onboardingStatus } = useGetOnboardingStatusQuery();
  const dataRequestStatus = onboardingStatus?.dataRequestStatus;
  const serverElapsed = onboardingStatus?.dataRequestElapsedSeconds ?? null;
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const isSyncing = dataRequestStatus === 'pending' || dataRequestStatus === 'processing';
  useEffect(() => {
    // Terminal: show the DB-frozen elapsed (fulfilled_at − requested_at) as-is, no ticking.
    if (!isSyncing) {
      if (serverElapsed != null && serverElapsed >= 0) setElapsedSeconds(serverElapsed);
      return;
    }
    // Running: seed from the DB elapsed at this status call, then increment locally each second.
    // Re-runs whenever serverElapsed changes (next status refetch) → re-seeds, so it stays in sync
    // without ever comparing a client clock to a server timestamp.
    if (serverElapsed == null) return;
    const base = Math.max(0, serverElapsed);
    const receivedAt = Date.now();
    const tick = () => {
      setElapsedSeconds(base + Math.floor((Date.now() - receivedAt) / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isSyncing, serverElapsed]);
  return { elapsedSeconds, running: isSyncing, dataRequestStatus };
}

function formatElapsed(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

/**
 * Pure renderer of the workflow-progress log. It reads the shared SSE event stream via
 * useHealthWalletSync() (RTK ref-counted → ONE connection shared across every consumer).
 * All side-effects — refetching the dashboard + onboarding status on terminal events,
 * resetting the log per workflow, reconnecting — live in the engine
 * (getSSEEvents.onCacheEntryAdded in healthWalletSlice.ts). This component only displays.
 * Drop it in anywhere (records sync card, Settings drawer); no props, no own connection.
 */
export const DataRequestSSEUpdates: FC = () => {
  const { events } = useHealthWalletSync();
  // The stopwatch anchors to OnboardingState's dataRequestedAt while running and shows the
  // lastCompletedAt − dataRequestedAt delta at completion (see useSyncStopwatch).
  const { elapsedSeconds, running, dataRequestStatus } = useSyncStopwatch();
  // Pill palette: brand while running, soft green on completed, soft red on failed, neutral
  // otherwise — all off the DURABLE dataRequestStatus (not the SSE event buffer, which is
  // empty after a reload → the color would drop to neutral). Flips atomically with `running`.
  const pill = running
    ? { fg: 'primary.main', bg: 'common.white', border: 'divider' }
    : dataRequestStatus === 'completed'
      ? { fg: '#2e7d32', bg: '#e8f5e9', border: '#a5d6a7' }
      : dataRequestStatus === 'failed'
        ? { fg: '#c62828', bg: '#ffebee', border: '#ef9a9a' }
        : { fg: 'text.secondary', bg: 'common.white', border: 'divider' };
  const listRef = useRef<HTMLUListElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const previousEventCountRef = useRef(0);
  // Stick-to-bottom: auto-scroll follows new events ONLY while the user is at the bottom.
  // Scrolling up detaches it (so they can read history mid-run); scrolling back to the
  // bottom re-attaches it. Detection = IntersectionObserver on a bottom SENTINEL (visible ⇒
  // at bottom) and scrolling = sentinel.scrollIntoView() — both container-agnostic, unlike
  // the old `closest('[style*="overflow"]')` lookup, which only matched INLINE overflow
  // styles and silently failed in MUI sx-styled scroll containers (the Settings drawer).
  const stickToBottomRef = useRef(true);

  useEffect(() => {
    const sentinel = bottomRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => { stickToBottomRef.current = entry.isIntersecting; },
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Auto-scroll to bottom when new events arrive — unless the user scrolled away.
  // Runs post-render (before the observer's async callback), so the programmatic scroll
  // itself never flips the stick flag off.
  useEffect(() => {
    const currentEventCount = events.length;
    if (currentEventCount > previousEventCountRef.current && stickToBottomRef.current) {
      bottomRef.current?.scrollIntoView({ block: 'nearest' });
    }
    previousEventCountRef.current = currentEventCount;
  }, [events]);

  return (
    <>
      {events.length > 0 && (
        // Sticky stopwatch, flush with the top of the list: the sticky wrapper is HEIGHT 0 (takes
        // no layout space — the list starts at the very top) and only the small right-aligned pill
        // overlays the corner, so rows underneath stay readable (no full-width opaque band).
        <Box sx={{ position: 'sticky', top: 0, zIndex: 1, height: 0, overflow: 'visible', display: 'flex', justifyContent: 'flex-end' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              py: 0.75,
              minHeight: '1.75rem', // room for the 0.85rem text — border must not crowd it
              bgcolor: pill.bg, // solid — rows scrolling beneath must not show through
              border: '1px solid',
              borderColor: pill.border,
              borderRadius: 10,
              boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
            }}
          >
            <TickingClockIcon color={pill.fg as string} running={running} />
            <Typography
              variant="caption"
              sx={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.85rem',
                lineHeight: 1.2,
                color: pill.fg,
                // Always bold: the pill only renders while events exist, i.e. a run is in
                // flight or just finished — there's no neutral idle state to de-emphasize.
                fontWeight: 600,
              }}
            >
              {elapsedSeconds > 0 ? formatElapsed(elapsedSeconds) :  '00:00'}
            </Typography>
          </Box>
        </Box>
      )}
      <List dense ref={listRef} sx={{ py: 0, pt: 0, pb: 0, pl: 0, ml: 0 }}>
        {events.slice(-500).map((event: any, index: number) => (
          <Fade in timeout={300} key={index}>
            <ListItem sx={{ py: 0, minHeight: 'auto', alignItems: 'flex-start', pl: 0, ml: 0 }}>
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.1, fontSize: '0.8rem' }}>
                      {events.length - events.slice(-500).length + index + 1}. {event.data || event.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, fontSize: '0.7rem', ml: 1, whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {new Date(event.timestamp).toLocaleTimeString()}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          </Fade>
        ))}
      </List>
      {/* Bottom sentinel: scroll target for stick-to-bottom + IntersectionObserver anchor. */}
      <div ref={bottomRef} />
    </>
  );
};
